import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { Form } from '@edx/paragon';
import { ReactComponent as RadioIcon } from '../../profile/assets/radio-button.svg';
import { ReactComponent as RadioEmptyIcon } from '../../profile/assets/radio-button-empty.svg';

import messages from './ProfSphere.messages';

// Components
import FormControls from './elements/FormControls';
import EditableItemHeader from './elements/EditableItemHeader';
import EmptyContent from './elements/EmptyContent';
import SwitchContent from './elements/SwitchContent';
import CustomSelect from '../components/CustomSelect/CustomSelect';

const ProfSphere = ({
  formId,
  profSphere,
  saveState,
  error,
  intl,
  spheres,
  changeHandler,
  submitHandler,
  closeHandler,
  openHandler,
  changeData,
  saveData,
  onSaveComplete,
  editMode: initialEditMode,
}) => {
  const [editMode, setEditMode] = useState(changeData ? 'editing' : initialEditMode);
  const [selectedValue, setSelectedValue] = useState(profSphere?.id?.toString() || '');

  useEffect(() => {
    setSelectedValue(profSphere?.id?.toString() || '');
  }, [profSphere]);

  useEffect(() => {
    if (changeData) {
      setEditMode('editing');
    } else {
      handleClose();
    }
  }, [changeData]);

  useEffect(() => {
    if (saveData) {
      handleSubmit();
    }
  }, [saveData]);

  const handleChange = value => {
    setSelectedValue(value);

    // Find the complete sphere object from the spheres array
    const selectedSphere = spheres.find(sphere => sphere.id.toString() === value);
    const sphereObject = selectedSphere || null;

    changeHandler(formId, sphereObject);
  };

  const handleSubmit = e => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    submitHandler(formId);
    if (onSaveComplete) {
      onSaveComplete();
    }
  };

  const handleClose = () => {
    closeHandler(formId);
    setEditMode('static');
  };

  const handleOpen = () => {
    openHandler(formId);
    setEditMode('editing');
  };

  const sphereOptions = [
    {
      value: '',
      label: '——',
      displayLabel: intl.formatMessage(messages['profile.profSpheres.option.default']),
    },
    ...spheres.map(({ id, name }) => ({
      value: id.toString(),
      label: name,
      displayLabel: name,
    })),
  ];

  const renderOption = (option, currentValue) => {
    const isSelected = option.value === currentValue;
    const Icon = isSelected ? RadioIcon : RadioEmptyIcon;

    return (
      <>
        <Icon className="tw-w-4 tw-h-4 tw-min-w-4 tw-min-h-4" />
        <span className={option.value === '' ? 'tw-text-neutral-500' : ''}>{option.label}</span>
      </>
    );
  };

  return (
    <SwitchContent
      className="tw-mb-10"
      expression={editMode}
      cases={{
        editing: (
          <div role="dialog" aria-labelledby={`${formId}-label`}>
            <form onSubmit={handleSubmit}>
              <Form.Group controlId={formId} isInvalid={error !== null} className="tw-mb-0">
                <p className="!tw-mb-2 tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[100%]">
                  {intl.formatMessage(messages['profile.profSpheres.label'])}
                </p>
                <p className="tw-text-[14px] tw-text-placeholder tw-font-medium !tw-mb-[14px] tw-leading-[120%]">
                  {intl.formatMessage(messages['profile.profSpheres.placeholder'])}
                </p>
                <CustomSelect
                  options={sphereOptions}
                  value={selectedValue}
                  onChange={handleChange}
                  placeholder={intl.formatMessage(messages['profile.profSpheres.option.default'])}
                  renderOption={renderOption}
                  getOptionLabel={option => option.displayLabel}
                  getOptionValue={option => option.value}
                  enableSearch={false}
                />
                {error !== null && (
                  <Form.Control.Feedback hasIcon={false}>{error}</Form.Control.Feedback>
                )}
              </Form.Group>
              <div className="tw-hidden">
                <FormControls
                  saveState={saveState}
                  cancelHandler={handleClose}
                  changeHandler={handleChange}
                  saveData={saveData}
                  visibilityId={formId}
                />
              </div>
            </form>
          </div>
        ),
        editable: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.profSpheres.label'])}
              showEditButton
              onClickEdit={handleOpen}
            />
            <p data-hj-suppress className="h5">
              {profSphere?.name}
            </p>
          </>
        ),
        empty: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.profSpheres.label'])}
            />
            <EmptyContent onClick={handleOpen}>
              {intl.formatMessage(messages['profile.profSpheres.empty'])}
            </EmptyContent>
          </>
        ),
        static: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.profSpheres.label'])}
            />
            <p
              data-hj-suppress
              className="tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[150%]"
            >
              {profSphere?.name || (
                <span className="tw-text-[14px] tw-text-placeholder tw-font-medium">
                  {intl.formatMessage(messages['profile.profSpheres.placeholder'])}
                </span>
              )}
            </p>
          </>
        ),
      }}
    />
  );
};

ProfSphere.propTypes = {
  formId: PropTypes.string.isRequired,
  profSphere: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  editMode: PropTypes.oneOf(['editing', 'editable', 'empty', 'static']),
  saveState: PropTypes.string,
  error: PropTypes.string,
  spheres: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      is_active: PropTypes.bool,
    }),
  ).isRequired,
  changeHandler: PropTypes.func.isRequired,
  submitHandler: PropTypes.func.isRequired,
  closeHandler: PropTypes.func.isRequired,
  openHandler: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  changeData: PropTypes.bool,
  saveData: PropTypes.bool,
  onSaveComplete: PropTypes.func,
};

ProfSphere.defaultProps = {
  editMode: 'static',
  saveState: null,
  profSphere: { id: '', name: '' },
  error: null,
  changeData: false,
  saveData: false,
  onSaveComplete: null,
};

export default connect(null, {})(injectIntl(ProfSphere));
